import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { computeProgress, parseDod } from '../../components/projects/helpers';
import type { ProjectRow } from '../../components/projects/types';

export interface ProjectListItem {
  project: ProjectRow;
  leadName: string | null;
  doneCount: number;
  totalCount: number;
  dodMet: number;
  dodTotal: number;
}

export type ListState = 'loading' | 'ready' | 'error' | 'no-org';

export interface UseProjectsList {
  state: ListState;
  items: ReadonlyArray<ProjectListItem>;
  error: string | null;
  reload: () => void;
}

export function useProjectsList(activeOrgId: string | null): UseProjectsList {
  const [state, setState] = useState<ListState>('loading');
  const [items, setItems] = useState<ReadonlyArray<ProjectListItem>>([]);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!activeOrgId) {
      setState('no-org');
      setItems([]);
      return;
    }
    let cancelled = false;
    setState('loading');
    setError(null);

    (async () => {
      const [projectsRes, membersRes, wiRes] = await Promise.all([
        supabase
          .from('projects')
          .select(
            'id, org_id, name, description, status, lead_id, target_date, definition_of_done, created_at, created_by, updated_at, updated_by, archived_at'
          )
          .eq('org_id', activeOrgId)
          .is('archived_at', null)
          .order('created_at', { ascending: false }),
        supabase.rpc('list_org_members', { p_org_id: activeOrgId }),
        supabase
          .from('work_items')
          .select('id, type, status, project_id')
          .eq('org_id', activeOrgId)
          .not('project_id', 'is', null)
      ]);

      if (cancelled) return;
      if (projectsRes.error) {
        setError(projectsRes.error.message);
        setState('error');
        return;
      }

      const nameById = new Map<string, string>();
      const members = (membersRes.data ?? []) as ReadonlyArray<Record<string, unknown>>;
      for (const m of members) {
        const id = (m.user_id ?? m.id) as string | undefined;
        const name = (m.display_name ?? m.full_name ?? m.name ?? m.email) as
          | string
          | undefined;
        if (id && name) nameById.set(id, name);
      }

      const wiByProject = new Map<string, { type: string; status: string }[]>();
      for (const w of (wiRes.data ?? []) as { type: string; status: string; project_id: string }[]) {
        const arr = wiByProject.get(w.project_id) ?? [];
        arr.push({ type: w.type, status: w.status });
        wiByProject.set(w.project_id, arr);
      }

      const next: ProjectListItem[] = (projectsRes.data ?? []).map((p) => {
        const wis = wiByProject.get(p.id) ?? [];
        const { done, total } = computeProgress(wis);
        const dod = parseDod(p.definition_of_done);
        return {
          project: p as ProjectRow,
          leadName: p.lead_id ? (nameById.get(p.lead_id) ?? null) : null,
          doneCount: done,
          totalCount: total,
          dodMet: dod.filter((c) => c.met).length,
          dodTotal: dod.length
        };
      });

      setItems(next);
      setState('ready');
    })().catch((e) => {
      if (cancelled) return;
      setError(e instanceof Error ? e.message : String(e));
      setState('error');
    });

    return () => {
      cancelled = true;
    };
  }, [activeOrgId, tick]);

  return { state, items, error, reload: () => setTick((t) => t + 1) };
}
